import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import rem from 'polished/lib/helpers/rem'
import styled from 'styled-components'
import Label from 'LuxKit/Label'
import TextLink from 'LuxKit/TextLink'
import ReserveForZeroPolicyModalContent from './ReserveForZeroPolicyModalContent'
import switchFunc from 'lib/function/switchFunc'
import { depositAmountStringBuilder } from 'lib/tours/tourUtils'
import DepositInfoModalContent from '../DepositInfo/DepositInfoModalContent'

import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { isOfferDefaultDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { useAppSelector } from 'hooks/reduxHooks'

enum ModalSteps {
  deposit = 'deposit',
  reserve = 'reserve',
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

const Section = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(20)};

  &:nth-child(2) {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    width: 50%;

    &:nth-child(2) {
      margin-top: 0;
      margin-left: ${rem(20)};
    }
  }
`

const SectionSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(20)};
`

const TopSection = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
`

const BottomSection = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  margin-top: ${rem(16)};
`

interface Props {
  offerType: App.OfferType
  depositAmountPercentage: number
  depositType?: App.Tours.V2OfferDepositType
  depositThresholds?: App.DepositThreshold
  depositAmount?: number
}

function ReserveForZeroAndDepositPolicyModal({
  depositAmountPercentage,
  depositType,
  depositThresholds,
  offerType,
  depositAmount,
}: Props) {
  const intl = useIntl()
  const [step, setStep] = useState<ModalSteps>()

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const depositShortLabel = !depositType ? undefined : depositAmountStringBuilder(defaultDepositAmountPercentage, depositType, depositAmountPercentage, intl).short

  const depositTitleString = isOfferUsingDefaultDepositPercentage
    ? intl.formatMessage({
        id: '-diVs4yUkPvAdC_jhGWi',
        defaultMessage: 'a deposit',
        description: 'Generic deposit label used in modal title dialog window',
      })
    : !depositType
        ? intl.formatMessage(
            {
              id: 'G9ZZbm01efVaZ1tw6N4i',
              defaultMessage: '{percentage}% deposit',
              description: 'Percentage-based deposit label for modal title in a modal dialog',
            },
            { percentage: depositAmountPercentage },
          )
        : intl.formatMessage(
            {
              id: '4WfOS0p8gp8_0TH6U1LO',
              defaultMessage: '{depositLabel} deposit',
              description: 'Deposit label with amount for modal title dialog window',
            },
            { depositLabel: depositShortLabel },
          )

  const depositAmountString = isOfferUsingDefaultDepositPercentage
    ? intl.formatMessage({
        id: '8U7nW2tyQiEohtk8pL5J',
        defaultMessage: 'deposit',
        description: 'Generic deposit label used in deposit amount context',
      })
    : !depositType
        ? intl.formatMessage(
            {
              id: 'pgZZpt9ZvHYMRmar57KR',
              defaultMessage: '{percentage}% deposit',
              description: 'Percentage-based deposit label for deposit amount context',
            },
            { percentage: depositAmountPercentage },
          )
        : intl.formatMessage(
            {
              id: 'XIj5fatdqyUejXwDaTx4',
              defaultMessage: '{depositLabel} deposit',
              description: 'Deposit label with amount for deposit amount context',
            },
            { depositLabel: depositShortLabel },
          )

  const title = switchFunc({
    deposit: intl.formatMessage({
      id: 'Dv-kn7M6BQIhBRhqDpDI',
      defaultMessage: 'Pay {depositTitleString}',
      description: 'Modal title for pay deposit step in a modal dialog',
    }, { depositTitleString }),
    reserve: intl.formatMessage({
      id: 'IbtTnAP74D1ZCM_Cv8Jo',
      defaultMessage: 'Reserve now for free',
      description: 'Modal title for reserve for the free step in a modal dialog',
    }),
  }, intl.formatMessage({
    id: 'Rtp1WbOZPQolhTlmGEhl',
    defaultMessage: 'Flexible payment options',
    description: 'Modal title for flexible payment options overview in a modal dialog',
  }))(step)

  const subtitle = switchFunc({
    deposit: undefined,
    reserve: undefined,
  }, intl.formatMessage({
    id: 'CA71jWk3I7dpOaws_Xx5',
    defaultMessage: 'Book with ease by choosing one of our flexible payment methods below.',
    description: 'Modal subtitle for flexible payment options overview',
  }))(step as string)

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      size="L"
      onBack={step ? () => setStep(undefined) : undefined}
    >
      {step === ModalSteps.reserve && (
        <ReserveForZeroPolicyModalContent />
      )}
      {step === ModalSteps.deposit && (
        <DepositInfoModalContent
          offerType={offerType}
          depositAmountPercentage={depositAmountPercentage}
          depositThresholds={depositThresholds}
          depositAmount={depositAmount}
        />
      )}
      {!step && (
        <SectionContainer>
          <Section>
            <TopSection>
              <Heading variant="heading6">
                <FormattedMessage
                  id="ghp2aDTzFlDHBeOVxQIe"
                  defaultMessage="Reserve now for free"
                  description="Section heading for reserve for free option in a modal dialog"
                />
              </Heading>
              <BodyText variant="medium">
                <FormattedMessage
                  id="-t7cRXz3PDNLUS8VuLHc"
                  defaultMessage="We’ll hold your reservation for 48 hours so you have time to think it over."
                  description="Body copy text explaining 48 hour reservation hold"
                />
              </BodyText>
              <div>
                <Label kind="tertiary" variant="warning">
                  <FormattedMessage
                    id="qezE6ICLb0cNxJU-FbzL"
                    defaultMessage="Limit of 2 reservations per member"
                    description="Warning label about reservation limit in a modal dialog"
                  />
                </Label>
              </div>
            </TopSection>
            <BottomSection>
              <TextLink onClick={() => setStep(ModalSteps.reserve)} size="large">
                <FormattedMessage
                  id="PPCGiazAPW3-mFZb0jjN"
                  defaultMessage="Learn more"
                  description="Hyperlink text to learn more about reserve for free"
                />
              </TextLink>
            </BottomSection>
          </Section>
          <SectionSeparator>
            <Heading variant="heading6">
              <FormattedMessage
                id="QmDKq0L6ZfEc2i-F6Ehu"
                defaultMessage="Or"
                description="Separator between reserve and deposit options in a modal dialog"
              />
            </Heading>
          </SectionSeparator>
          <Section>
            <TopSection>
              <Heading variant="heading6">
                <FormattedMessage
                  id="6nyhxeGxF1HX7v3xdnec"
                  defaultMessage="Pay {depositTitleString} or in full today"
                  description="Section heading for pay deposit or full amount option"
                  values={{ depositTitleString }}
                />
              </Heading>
              <BodyText variant="medium">
                <FormattedMessage
                  id="vcDBJoA880YtI92crHoE"
                  defaultMessage="Secure your dates by paying the full amount upfront or with a {depositAmountString} of your accommodation booking."
                  description="Body text explaining deposit payment option in the booking flow"
                  values={{ depositAmountString }}
                />
              </BodyText>
            </TopSection>
            <BottomSection>
              <TextLink onClick={() => setStep(ModalSteps.deposit)} size="large">
                <FormattedMessage
                  id="pZAP3iHzhY9ko7MazNlB"
                  defaultMessage="Learn more"
                  description="Link text to learn more about deposit option for deposit payment"
                />
              </TextLink>
            </BottomSection>
          </Section>
        </SectionContainer>
      )}
    </Modal>
  )
}

export default ReserveForZeroAndDepositPolicyModal
